<template>
  <Teleport to="body">
    <div class="toast-container">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast"
          :class="`toast-${toast.type}`"
          @mouseenter="pauseToast(toast.id)"
          @mouseleave="resumeToast(toast.id)"
        >
          <div class="toast-icon">
            {{ typeIcons[toast.type] }}
          </div>
          <div class="toast-content">
            <div v-if="toast.title" class="toast-title">{{ toast.title }}</div>
            <div class="toast-message">{{ toast.message }}</div>
          </div>
          <button
            v-if="toast.autoClose === 0"
            class="toast-close"
            @click="removeToast(toast.id)"
          >
            ✕
          </button>
          <div
            v-else
            class="toast-progress"
            :style="{ animationDuration: toast.autoClose + 'ms' }"
          />
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { useToastStore } from '../stores/toast.js'

const toastStore = useToastStore()
const toasts = computed(() => toastStore.toasts)

const typeIcons = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ'
}

function removeToast(id) {
  toastStore.removeToast(id)
}

function pauseToast(id) {
  // CSS handles pause via :hover
}

function resumeToast(id) {
  // CSS handles resume
}
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 400px;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  background: white;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.1);
  pointer-events: auto;
  position: relative;
  overflow: hidden;
  min-width: 280px;
  border-left: 4px solid;
}

.toast-success {
  border-left-color: #22c55e;
}
.toast-error {
  border-left-color: #ef4444;
}
.toast-warning {
  border-left-color: #f59e0b;
}
.toast-info {
  border-left-color: #3b82f6;
}

.toast-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.toast-success .toast-icon { background: #dcfce7; color: #16a34a; }
.toast-error .toast-icon { background: #fee2e2; color: #dc2626; }
.toast-warning .toast-icon { background: #fef3c7; color: #d97706; }
.toast-info .toast-icon { background: #dbeafe; color: #2563eb; }

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #1e293b;
  margin-bottom: 0.15rem;
}

.toast-message {
  font-size: 0.85rem;
  color: #475569;
  line-height: 1.4;
}

.toast-close {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
  transition: all 0.15s;
}

.toast-close:hover {
  background: #f1f5f9;
  color: #475569;
}

.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: currentColor;
  opacity: 0.2;
  animation: toast-progress linear forwards;
}

.toast-success .toast-progress { background: #22c55e; }
.toast-error .toast-progress { background: #ef4444; }
.toast-warning .toast-progress { background: #f59e0b; }
.toast-info .toast-progress { background: #3b82f6; }

@keyframes toast-progress {
  from { width: 100%; }
  to { width: 0%; }
}

.toast:hover .toast-progress {
  animation-play-state: paused;
}

/* Transitions */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
  margin-bottom: -50px;
}
</style>